// src/domains/core/dashboard/components/widgets/insights/InsightCard.tsx

import type { Insight } from "@/domains/core/context-intelligence/types/insights.types";

import { Info, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";
import { type FC } from "react";

import { Card, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

export interface InsightCardProperties {
  className?: string;
  insight: Insight;
  onDetailsClick?: (insight: Insight) => void;
}

/**
 * Get priority color classes for insight priority
 *
 * @param priority - Insight priority level
 *
 * @returns {string} CSS classes for priority color
 *
 */
const getPriorityColor = (priority: Insight["priority"]): string => {
  switch (priority) {
    case "critical": {
      return "bg-red-500/20 text-red-400 border-red-500/30";
    }
    case "high": {
      return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    }
    case "medium": {
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    }
    case "low": {
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    }
    case "background": {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
    default: {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  }
};

/**
 * Get category icon for insight category
 *
 * @param category - Insight category
 *
 * @returns {JSX.Element} Category icon component
 *
 */
const getCategoryIcon = (category: Insight["category"]): React.JSX.Element => {
  switch (category) {
    case "risk": {
      return <AlertTriangle className="w-4 h-4" />;
    }
    case "opportunity": {
      return <TrendingUp className="w-4 h-4" />;
    }
    case "performance": {
      return <CheckCircle className="w-4 h-4" />;
    }
    default: {
      return <Info className="w-4 h-4" />;
    }
  }
};

/**
 * Format timestamp to German locale date string
 *
 * @param timestamp - Unix timestamp in milliseconds
 *
 * @returns {string} Formatted date string
 *
 */
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
  });
};

/**
 * WHAT:
 *   Card component that displays a single contextual insight with priority
 *   indicators, category icons, and recommendations.
 *
 * WHY:
 *   Enables users to see and understand contextual insights at a glance.
 *
 * PROPS:
 *   - insight: Insight — Insight data
 *   - onDetailsClick?: (insight) => void — Details click handler
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Displays insight title and description
 *   - Shows priority badge with color coding
 *   - Displays category icon
 *   - Shows metadata (date, relevance, confidence)
 *   - Lists recommendations (up to 3)
 *   - Provides details button
 *
 * RISKS:
 *   - NOTE: Priority color mapping may not match design system
 *   - NOTE: Date formatting is hardcoded to German locale - may not match user preferences
 *   - NOTE: Recommendations display is limited to 3 - may miss important items
 *
 * @param root0 - Component props
 *
 * @param root0.insight - Insight data
 *
 * @param root0.onDetailsClick - Details click handler
 *
 * @param root0.className - Additional CSS classes
 *
 * @returns {JSX.Element} Insight card component
 *
 */
export const InsightCard: FC<InsightCardProperties> = ({ className, insight, onDetailsClick }) => {
  return (
    <Card
      className={cn(
        "border-slate-600/50 bg-slate-800/30 hover:bg-slate-800/50 transition-all",
        className,
      )}
      padding="md"
      variant="default"
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {getCategoryIcon(insight.category)}
              <h4 className="text-sm font-semibold text-white truncate">{insight.title}</h4>
            </div>
            <p className="text-xs text-gray-400 text-ellipsis line-clamp-2">
              {insight.description}
            </p>
          </div>
          <Badge
            className={cn("text-xs px-2 py-1 flex-shrink-0", getPriorityColor(insight.priority))}
            variant="outline"
          >
            {insight.priority}
          </Badge>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-gray-500">{formatDate(insight.generatedAt)}</span>
          <span className="text-gray-500">•</span>
          <span className="text-gray-400">Relevance: {(insight.relevance * 100).toFixed(0)}%</span>
          <span className="text-gray-500">•</span>
          <span className="text-gray-400">
            Confidence: {(insight.confidence * 100).toFixed(0)}%
          </span>
        </div>

        {/* Recommendations */}
        {insight.metadata?.recommendations && insight.metadata.recommendations.length > 0 ? (
          <div className="pt-2 border-t border-slate-700/50">
            <p className="text-xs font-medium text-gray-400 mb-1">Recommendations:</p>
            <ul className="text-xs text-gray-500 space-y-1">
              {insight.metadata.recommendations.slice(0, 3).map((rec) => (
                <li className="flex items-start gap-2" key={`${insight.id}-${rec}`}>
                  <span className="text-tracq-bright-blue mt-0.5">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Details Button */}
        {onDetailsClick ? (
          <button
            className="w-full text-xs text-tracq-bright-blue hover:text-tracq-deep-blue transition-colors text-left"
            onClick={() => {
              onDetailsClick(insight);
            }}
          >
            Why? →
          </button>
        ) : null}
      </div>
    </Card>
  );
};
